import type { QueryClient } from '@tanstack/react-query';
import {
  VIEW_TOPICS,
  DEPOSITS_VIEW_TOPICS,
  CONFIRMATIONS_VIEW_TOPICS,
  OPS_VIEW_TOPICS,
  FLOOR_VIEW_TOPICS,
  ROSTER_VIEW_TOPICS,
  MESSENGER_VIEW_TOPICS,
  NO_CANCEL,
} from '@/api/liveStream';

/**
 * The write-side twin of liveStream.ts::handleOutboxEvent. When the client's
 * OWN write to an entity succeeds, this applies the SAME entity→query-key
 * mapping the /ws outbox path uses — but optimistically: where the delta is
 * known (the studio-scoped calendar/availability bundles) it PATCHES the cached
 * data in place with `setQueriesData` (a prefix match, so every mounted week
 * window updates with no round-trip — that is what makes an own write visible
 * even when /ws is down, tv-lop); everywhere else it fires a SINGLE scoped
 * `invalidateQueries` for exactly the network-wide keys the entity feeds. It
 * NEVER calls the bare all-queries `invalidateQueries()`.
 *
 * It deliberately reuses liveStream's exported topic sets so the read (/ws) and
 * write (own-mutation) reactivity paths share one source of truth and cannot
 * drift. applyWriteToCache is the SUCCESS path; invalidateWriteKeys below is the
 * REJECT/revert twin — it shares the same entity→keys derivation but INVALIDATES
 * (never patches) so refused writes refetch server truth. It is called from
 * persistenceBridge.ts::handleWriteReject (tv-jp2).
 */

type Bundle = Record<string, unknown>;

/**
 * Studio-scoped view bundles keyed by ['<key>', studioId, from, to]. Each holds
 * only the sub-collections listed, so an own write to one of them is reflected
 * by merging the record into that sub-collection (or removing the id on delete).
 * calendar's writable sub-collections are exactly VIEW_TOPICS; availability's
 * bundle is {bookings, artists, studios}, so only a `bookings` write changes it.
 */
const STUDIO_VIEWS: ReadonlyArray<{ key: string; subcollections: ReadonlySet<string> }> = [
  { key: 'calendar', subcollections: VIEW_TOPICS },
  { key: 'availability', subcollections: new Set(['bookings']) },
];

/**
 * Network-wide views (no per-studio query key) ← the topic sets that feed them,
 * imported from liveStream. An own write to any topic in a set scoped-
 * invalidates that single key exactly once.
 */
const NETWORK_VIEWS: ReadonlyArray<{ key: string; topics: ReadonlySet<string> }> = [
  { key: 'deposits', topics: DEPOSITS_VIEW_TOPICS },
  { key: 'confirmations', topics: CONFIRMATIONS_VIEW_TOPICS },
  { key: 'ops', topics: OPS_VIEW_TOPICS },
  { key: 'floor', topics: FLOOR_VIEW_TOPICS },
  { key: 'roster', topics: ROSTER_VIEW_TOPICS },
  { key: 'messenger', topics: MESSENGER_VIEW_TOPICS },
];

/**
 * The single entity→keys derivation both reactivity paths share, so the SUCCESS
 * path (applyWriteToCache) and the REJECT path (invalidateWriteKeys) can never
 * drift. `studioViewsFor` returns the studio-scoped view bundles that hold the
 * entity (their query key is ['<key>', studioId, …]); `networkViewsFor` returns
 * the network-wide views the entity feeds (their query key is ['<key>']).
 */
function studioViewsFor(entity: string): ReadonlyArray<{ key: string }> {
  return STUDIO_VIEWS.filter((view) => view.subcollections.has(entity));
}
function networkViewsFor(entity: string): ReadonlyArray<{ key: string }> {
  return NETWORK_VIEWS.filter((view) => view.topics.has(entity));
}

/** The studioId a record carries, or undefined when unknown (e.g. a delete/create-revert). */
function studioIdOf(record: unknown | undefined): string | undefined {
  return record && typeof record === 'object'
    ? (record as { studioId?: string }).studioId
    : undefined;
}

/**
 * Returns a copy of `prev` with the entity's sub-collection updated: `record`
 * present merges/replaces the id, `record === undefined` removes it. Returns
 * `prev` unchanged when the query has no data yet, so no phantom bundle is
 * created.
 */
function patchBundle(
  prev: Bundle | undefined,
  subcollection: string,
  id: string,
  record: unknown | undefined,
): Bundle | undefined {
  if (!prev) return prev;
  const coll = { ...((prev[subcollection] as Record<string, unknown> | undefined) ?? {}) };
  if (record === undefined) {
    delete coll[id];
  } else {
    coll[id] = record;
  }
  return { ...prev, [subcollection]: coll };
}

/**
 * Reflect an own write of `entity`/`id` in the query cache: patch at once, then
 * invalidate. `record` undefined means a delete. Used where the write is already
 * on the server (a server-authored intent result) or will never be sent (a
 * local-only placeholder). A queued PUT uses the two halves separately — patch
 * now, `invalidateWriteViews` once the PUT lands (B2) — so a view never refetches
 * the old server row and shows it over the own write.
 */
export function applyWriteToCache(
  qc: QueryClient,
  entity: string,
  id: string,
  record: unknown | undefined,
): void {
  patchWriteIntoCache(qc, entity, id, record);
  invalidateWriteViews(qc, entity, record);
}

/**
 * The optimistic half: merge the record into the studio-scoped bundles whose
 * delta is known (a delete patches every mounted window). Never invalidates, so a
 * mounted view shows the own write with no /ws frame and no refetch.
 */
export function patchWriteIntoCache(
  qc: QueryClient,
  entity: string,
  id: string,
  record: unknown | undefined,
): void {
  const studioId = studioIdOf(record);
  for (const view of studioViewsFor(entity)) {
    if (record === undefined) {
      // Delete: the removed record's studioId is gone, so patch every mounted
      // window of every studio's bundle (a no-op on bundles that never held
      // the id — cheaper and safer than a refetch).
      qc.setQueriesData({ queryKey: [view.key] }, (prev: unknown) =>
        patchBundle(prev as Bundle | undefined, entity, id, undefined),
      );
    } else if (studioId) {
      // Prefix match so whatever week window is mounted for this studio gets
      // the merge.
      qc.setQueriesData({ queryKey: [view.key, studioId] }, (prev: unknown) =>
        patchBundle(prev as Bundle | undefined, entity, id, record),
      );
      // The all-studios calendar (75n8) holds every studio's records too.
      if (view.key === 'calendar') {
        qc.setQueriesData({ queryKey: [view.key, 'all'] }, (prev: unknown) =>
          patchBundle(prev as Bundle | undefined, entity, id, record),
        );
      }
    }
  }
}

/**
 * The refetch half: scoped-invalidate the views an own write feeds but cannot
 * patch — a studio view when the record's studioId is unknown, and each
 * network-wide view once. Never a bare all-queries invalidate.
 */
export function invalidateWriteViews(qc: QueryClient, entity: string, record: unknown | undefined): void {
  if (record !== undefined && !studioIdOf(record)) {
    for (const view of studioViewsFor(entity)) void qc.invalidateQueries({ queryKey: [view.key] }, NO_CANCEL);
  }
  for (const view of networkViewsFor(entity)) {
    void qc.invalidateQueries({ queryKey: [view.key] }, NO_CANCEL);
  }
}

/**
 * The reject/revert twin of applyWriteToCache. When the client's OWN write is
 * REFUSED (a 409 booking-conflict or a 422 domain-validation), the optimistic
 * brainStore mutation is reverted (persistenceBridge.ts) and the optimistic
 * query-cache patch must be discarded so the affected views refetch server
 * truth — so this INVALIDATES (never patches) exactly the view keys the entity
 * feeds, using the SAME entity→keys derivation as applyWriteToCache. `record` is
 * the prior server value (`was`); its studioId scopes the studio views when
 * known. It NEVER calls the bare all-queries `invalidateQueries()`.
 */
export function invalidateWriteKeys(
  qc: QueryClient,
  entity: string,
  record: unknown | undefined,
): void {
  const studioId = studioIdOf(record);

  // Studio-scoped views: invalidate the one studio's key when known, else the
  // whole view key — still scoped to that view, never a bare all-queries wipe.
  for (const view of studioViewsFor(entity)) {
    void qc.invalidateQueries({ queryKey: studioId ? [view.key, studioId] : [view.key] }, NO_CANCEL);
    if (studioId && view.key === 'calendar') void qc.invalidateQueries({ queryKey: [view.key, 'all'] }, NO_CANCEL);
  }

  // Network-wide views: one scoped invalidate per affected key, at most once.
  for (const view of networkViewsFor(entity)) {
    void qc.invalidateQueries({ queryKey: [view.key] }, NO_CANCEL);
  }
}
